// Core modules
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import {
  ErrorHandler,
  NgModule,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
// End Core modules

// Third party modules
import { provideDatadogRouter } from '@datadog/browser-rum-angular';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AgGridModule } from 'ag-grid-angular';
import { ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';

import { AdminAdminComponent } from './admin/admin-admin/admin-admin.component';
import { AdminAdminClientApConfigsComponent } from './admin/admin-admin/client-ap-configs/admin-admin-client-ap-configs.component';
import { AdminAdminEmailsComponent } from './admin/admin-admin/emails/admin-admin-emails.component';
import { AdminAdminEsignTemplatesComponent } from './admin/admin-admin/esign-templates/admin-admin-esign-templates.component';
import { AdminAdminSsoConnectionsComponent } from './admin/admin-admin/sso-connections/admin-admin-sso-connections.component';
import { AdminAdminSsoConnectionComponent } from './admin/admin-admin/sso-connections/sso-connection/admin-admin-sso-connection.component';
import { SsoConnectionDomainsCardComponent } from './admin/admin-admin/sso-connections/sso-connection/domains-card/sso-connection-domains-card.component';
import { SsoConnectionRoleMappingsCardComponent } from './admin/admin-admin/sso-connections/sso-connection/role-mappings-card/sso-connection-role-mappings-card.component';
import { AdminAdminTradesComponent } from './admin/admin-admin/trades/admin-admin-trades.component';
import { AdminAdminTradeCategoriesComponent } from './admin/admin-admin/trades/trade-categories/admin-admin-trade-categories.component';
import { AdminAdminTradeRequirementsComponent } from './admin/admin-admin/trades/trade-requirements/admin-admin-trade-requirements.component';
import { AdminAdminTradeSpecialtyCategoriesComponent } from './admin/admin-admin/trades/trade-specialty-categories/admin-admin-trade-specialty-categories.component';
import { AdminComponent } from './admin/admin.component';
import { AddClientComponent } from './admin/clients/add-client/add-client.component';
import { AdminClientAdminComponent } from './admin/clients/admin-client/admin-client-admin/admin-client-admin.component';
import { AdminClientApConfigsComponent } from './admin/clients/admin-client/admin-client-ap-configs/admin-client-ap-configs.component';
import { AdminClientClientRegisteredVendorsComponent } from './admin/clients/admin-client/admin-client-client-registered-vendors/admin-client-client-registered-vendors.component';
import { AdminClientContactsComponent } from './admin/clients/admin-client/admin-client-contacts/admin-client-contacts.component';
import { AdminClientExemptVendorsComponent } from './admin/clients/admin-client/admin-client-exempt-vendors/admin-client-exempt-vendors.component';
import { AdminClientNotesComponent } from './admin/clients/admin-client/admin-client-notes/admin-client-notes.component';
import { AdminClientServiceRegionsComponent } from './admin/clients/admin-client/admin-client-service-regions/admin-client-service-regions.component';
import { AdminClientSitesComponent } from './admin/clients/admin-client/admin-client-sites/admin-client-sites.component';
import { AdminClientVendorsComponent } from './admin/clients/admin-client/admin-client-vendors/admin-client-vendors.component';
import { AdminClientComponent } from './admin/clients/admin-client/admin-client.component';
import { ClientInfoComponent } from './admin/clients/admin-client/client-info/client-info.component';
import { ComplianceAutoLiabilityComponent } from './admin/clients/admin-client/compliance-settings/compliance-auto-liability/compliance-auto-liability.component';
import { ComplianceBusinessVerifiedCompanyBackgroundChecksComponent } from './admin/clients/admin-client/compliance-settings/compliance-business-verified-company-background-checks/compliance-business-verified-company-background-checks.component';
import { ComplianceCompanyBackgroundChecksComponent } from './admin/clients/admin-client/compliance-settings/compliance-company-background-checks/compliance-company-background-checks.component';
import { ComplianceGeneralLiabilityComponent } from './admin/clients/admin-client/compliance-settings/compliance-general-liability/compliance-general-liability.component';
import { ComplianceOwnerBackgroundChecksComponent } from './admin/clients/admin-client/compliance-settings/compliance-owner-background-checks/compliance-owner-background-checks.component';
import { ComplianceProfessionalLiabilityComponent } from './admin/clients/admin-client/compliance-settings/compliance-professional-liability/compliance-professional-liability.component';
import { ComplianceSettingsComponent } from './admin/clients/admin-client/compliance-settings/compliance-settings.component';
import { ComplianceTradeSpecificComplianceTiersComponent } from './admin/clients/admin-client/compliance-settings/compliance-trade-specific-compliance-tiers/compliance-trade-specific-compliance-tiers.component';
import { ComplianceUmbrellaLiabilityComponent } from './admin/clients/admin-client/compliance-settings/compliance-umbrella-liability/compliance-umbrella-liability.component';
import { ComplianceWorkersCompensationComponent } from './admin/clients/admin-client/compliance-settings/compliance-workers-compensation/compliance-workers-compensation.component';
import { ClientsComponent } from './admin/clients/clients.component';
import { AdminComplianceClientRegisteredVendorsComponent } from './admin/compliance/client-registered-vendors/admin-compliance-client-registered-vendors.component';
import { ComplianceBusinessReviewComponent } from './admin/compliance/compliance-review/compliance-business-review/compliance-business-review.component';
import { ComplianceFinalReviewComponent } from './admin/compliance/compliance-review/compliance-final-review/compliance-final-review.component';
import { AddEditCOIComponent } from './admin/compliance/compliance-review/compliance-insurance-review/add-edit-coi/add-edit-coi.component';
import { ComplianceInsuranceReviewComponent } from './admin/compliance/compliance-review/compliance-insurance-review/compliance-insurance-review.component';
import { ComplianceOwnerReviewComponent } from './admin/compliance/compliance-review/compliance-owner-review/compliance-owner-review.component';
import { ComplianceReviewComponent } from './admin/compliance/compliance-review/compliance-review.component';
import { ComplianceTradeReviewComponent } from './admin/compliance/compliance-review/compliance-trade-review/compliance-trade-review.component';
import { ComplianceComponent } from './admin/compliance/compliance.component';
import { AdminComplianceExemptVendorComponent } from './admin/compliance/exempt-vendors/admin-compliance-exempt-vendor.component';
import { AdminDashboardComponent } from './admin/dashboard/admin-dashboard/admin-dashboard.component';
import { ApSystemsComponent } from './admin/dashboard/dashboard-widgets/api-errors-widget/ap-systems/ap-systems.component';
import { ApiErrorsWidgetComponent } from './admin/dashboard/dashboard-widgets/api-errors-widget/api-errors-widget.component';
import { BusinessVerificationApiErrorsComponent } from './admin/dashboard/dashboard-widgets/api-errors-widget/business-verification-api-errors/business-verification-api-errors.component';
import { ExpiringInsuranceWidgetComponent } from './admin/dashboard/dashboard-widgets/expiring-insurance-widget/expiring-insurance-widget.component';
import { PendingReviewsWidgetComponent } from './admin/dashboard/dashboard-widgets/pending-reviews-widget/pending-reviews-widget.component';
import { QualityControlWidgetComponent } from './admin/dashboard/dashboard-widgets/quality-control-widget/quality-control-widget.component';
import { ResolvingVendorsWidgetComponent } from './admin/dashboard/dashboard-widgets/resolving-vendors-widget/resolving-vendors-widget.component';
import { ReviewingVendorsWidgetComponent } from './admin/dashboard/dashboard-widgets/reviewing-vendors-widget/reviewing-vendors-widget.component';
import { VendorRenewalsWidgetComponent } from './admin/dashboard/dashboard-widgets/vendor-renewals-widget/vendor-renewals-widget.component';
import { VendorStatusesChartWidgetComponent } from './admin/dashboard/dashboard-widgets/vendor-statuses-chart-widget/vendor-statuses-chart-widget.component';
import { VendorSuspensionsWidgetComponent } from './admin/dashboard/dashboard-widgets/vendor-suspensions-widget/vendor-suspensions-widget.component';
import { AdminDashboardsComponent } from './admin/dashboards/admin-dashboards.component';
import { NotificationCenterComponent } from './admin/notification/notification-center/notification-center.component';
import { ReportsComponent } from './admin/reports/reports.component';
import { AdminVendorAdminComponent } from './admin/vendors/admin-vendor/admin-vendor-admin/admin-vendor-admin.component';
import { AdminVendorClientsComponent } from './admin/vendors/admin-vendor/admin-vendor-clients/admin-vendor-clients.component';
import { AdminVendorContactsComponent } from './admin/vendors/admin-vendor/admin-vendor-contacts/admin-vendor-contacts.component';
import { AdminVendorDocumentsComponent } from './admin/vendors/admin-vendor/admin-vendor-documents/admin-vendor-documents.component';
import { AdminVendorInfoComponent } from './admin/vendors/admin-vendor/admin-vendor-info/admin-vendor-info.component';
import { AdminVendorInvoicesComponent } from './admin/vendors/admin-vendor/admin-vendor-invoices/admin-vendor-invoices.component';
import { AdminVendorNotesComponent } from './admin/vendors/admin-vendor/admin-vendor-notes/admin-vendor-notes.component';
import { AdminVendorOwnersComponent } from './admin/vendors/admin-vendor/admin-vendor-owners/admin-vendor-owners.component';
import { AdminVendorComponent } from './admin/vendors/admin-vendor/admin-vendor.component';
import { VendorsComponent } from './admin/vendors/vendors.component';
// End Third party modules

// Application modules
import { AppRoutingModule } from './app-routing.module';
// End Application modules

// Components
import { AppComponent } from './app.component';
import { ClientAdminAccountingSystemComponent } from './client/admin/accounting/accounting-system/client-admin-accounting-system.component';
import { ClientAdminAccountingAllApVendorsComponent } from './client/admin/accounting/all-ap-vendors/client-admin-accounting-all-ap-vendors.component';
import { ClientAdminAccountingComponent } from './client/admin/accounting/client-admin-accounting.component';
import { ClientAdminComponent } from './client/admin/client-admin.component';
import { ClientAdminComplianceExemptSetupComponent } from './client/admin/compliance-exempt-setup/client-admin-compliance-exempt-setup.component';
import { ClientAdminMyCompanyComponent } from './client/admin/my-company/client-admin-my-company.component';
import { ClientAdminPublicApiKeysComponent } from './client/admin/public-api-keys/client-admin-public-api-keys.component';
import { ClientAdminSpotlightVendorsComponent } from './client/admin/spotlight-vendors/client-admin-spotlight-vendors.component';
import { ClientReportsComponent } from './client/client-reports/client-reports.component';
import { ClientComponent } from './client/client.component';
import { ContractAddContractComponent } from './client/contracts/contract/contract-add-contract/contract-add-contract.component';
import { ContractReviewComponent } from './client/contracts/contract/contract-review/contract-review.component';
import { ContractSiteSelectionComponent } from './client/contracts/contract/contract-site-selection/contract-site-selection.component';
import { ContractVendorSelectionComponent } from './client/contracts/contract/contract-vendor-selection/contract-vendor-selection.component';
import { ContractComponent } from './client/contracts/contract/contract.component';
import { ContractsComponent } from './client/contracts/contracts.component';
import { ClientDashboardComponent } from './client/dashboard/client-dashboard/client-dashboard.component';
import { AccountingAlertsWidgetComponent } from './client/dashboard/dashboard-widgets/accounting-alerts-widget/accounting-alerts-widget.component';
import { AlertsMetricsWidgetComponent } from './client/dashboard/dashboard-widgets/alerts-metrics-widget/alerts-metrics-widget.component';
import { ContractsWidgetComponent } from './client/dashboard/dashboard-widgets/contracts-widget/contracts-widget.component';
import { MyVendorsWidgetComponent } from './client/dashboard/dashboard-widgets/my-vendors-widget/my-vendors-widget.component';
import { QuickSourceWidgetComponent } from './client/dashboard/dashboard-widgets/quick-source-widget/quick-source-widget.component';
import { SpotlightVendorsWidgetComponent } from './client/dashboard/dashboard-widgets/spotlight-vendors-widget/spotlight-vendors-widget.component';
import { VendorRatingsWidgetComponent } from './client/dashboard/dashboard-widgets/vendor-ratings-widget/vendor-ratings-widget.component';
import { VendorStatusesWidgetComponent } from './client/dashboard/dashboard-widgets/vendor-statuses-widget/vendor-statuses-widget.component';
import { VendorTradesWidgetComponent } from './client/dashboard/dashboard-widgets/vendor-trades-widget/vendor-trades-widget.component';
import { ViveDirectoryWidgetComponent } from './client/dashboard/dashboard-widgets/vive-directory-widget/vive-directory-widget.component';
import { QuicksourceAddVendorsComponent } from './client/quicksource/quicksource-creation/quicksource-add-vendors/quicksource-add-vendors.component';
import { QuicksourceCreationComponent } from './client/quicksource/quicksource-creation/quicksource-creation.component';
import { QuicksourceDatePricingInstructionsComponent } from './client/quicksource/quicksource-creation/quicksource-date-pricing-instructions/quicksource-date-pricing-instructions.component';
import { QuicksourceLocationAndContactComponent } from './client/quicksource/quicksource-creation/quicksource-location-and-contact/quicksource-location-and-contact.component';
import { QuicksourceReviewComponent } from './client/quicksource/quicksource-creation/quicksource-review/quicksource-review.component';
import { QuicksourceSiteSelectionComponent } from './client/quicksource/quicksource-creation/quicksource-site-selection/quicksource-site-selection.component';
import { QuicksourceComponent } from './client/quicksource/quicksource.component';
import { AddSiteComponent } from './client/sites/client-sites/add-site/add-site.component';
import { ClientSitesComponent } from './client/sites/client-sites/client-sites.component';
import { EditSiteComponent } from './client/sites/client-sites/edit-site/edit-site.component';
import { SiteAdminComponent } from './client/sites/client-sites/site/site-admin/site-admin.component';
import { SiteContactsComponent } from './client/sites/client-sites/site/site-contacts/site-contacts.component';
import { SiteDetailsComponent } from './client/sites/client-sites/site/site-details/site-details.component';
import { SiteUsersComponent } from './client/sites/client-sites/site/site-users/site-users.component';
import { SiteVendorsValidatedComponent } from './client/sites/client-sites/site/site-vendors-validated/site-vendors-validated.component';
// End Interceptors

// Directives
import { SiteVendorFilesComponent } from './client/sites/client-sites/site/site-vendors/site-vendor-files/site-vendor-files.component';
import { SiteVendorsComponent } from './client/sites/client-sites/site/site-vendors/site-vendors.component';
import { SiteComponent } from './client/sites/client-sites/site/site.component';
import { SiteFilesComponent } from './client/sites/site-files/site-files.component';
import { VendorRatingsComponent } from './client/vendor-ratings/vendor-ratings.component';
import { ClientMyVendorAdminComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-admin/client-my-vendor-admin.component';
import { ClientMyVendorContactsComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-contacts/client-my-vendor-contacts.component';
import { ClientMyVendorContractDetailsComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-contracts/client-my-vendor-contract-details/client-my-vendor-contract-details.component';
import { ClientMyVendorContractsComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-contracts/client-my-vendor-contracts.component';
import { ClientMyVendorDocumentsComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-documents/client-my-vendor-documents.component';
import { ClientMyVendorHistoryComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-history/client-my-vendor-history.component';
import { ClientMyVendorInsuranceComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-insurance/client-my-vendor-insurance.component';
import { ClientMyVendorQsbComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-qsb/client-my-vendor-qsb.component';
import { ProposalRequestComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-qsb/proposal-request/proposal-request.component';
import { ClientMyVendorRatingsComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-ratings/client-my-vendor-ratings.component';
import { ClientMyVendorSitesComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-sites/client-my-vendor-sites.component';
import { ClientMyVendorTradesAndLicensesComponent } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor-trades-and-licenses/client-my-vendor-trades-and-licenses.component';
import { ClientMyVendor } from './client/vendors/client-my-vendors/client-my-vendor/client-my-vendor.component';
import { SpecificVendorComplianceFindingDropdownComponent } from './client/vendors/client-my-vendors/client-my-vendor/specific-vendor-compliance-finding-dropdown/specific-vendor-compliance-finding-dropdown.component';
import { ClientMyVendorsComponent } from './client/vendors/client-my-vendors/client-my-vendors.component';
import { ClientVendorsComponent } from './client/vendors/client-vendors.component';
import { ClientViveVendorsComponent } from './client/vendors/client-vive-vendors/client-vive-vendors.component';
import { CurrencyInputDirective } from './directives/currency-input/currency-input.directive';
import { FormControlNavtiveElementDirective } from './directives/form-control-native-element/form-control-navtive-element.directive';
import { InputMaskCustomModule } from './directives/input-mask/input-mask-custom.module';
import { NgbDateInputMaskDirective } from './directives/ngb-date-input-mask/ngb-date-input-mask.directive';
import { RequiredAsteriskDirective } from './directives/required-asterisk.directive';
import { StatusFieldChangesDirective } from './directives/status-style-changes/status-style-changes.directive';
import { FooterComponent } from './footer/footer.component';
import { AddressFormComponent } from './forms/address-form/address-form.component';
import { ApConfigsGridComponent } from './grids/ap-configs-grid/ap-configs-grid.component';
import { ApVendorsGridComponent } from './grids/ap-vendors-grid/ap-vendors-grid.component';
import { BaseGridComponent } from './grids/base-grid/base-grid.component';
import { BusinessVerificationApiErrorsGridComponent } from './grids/business-verification-api-errors-grid/business-verification-api-errors-grid.component';
import { ClientsGridComponent } from './grids/clients-grid/clients-grid.component';
import { CompanyFeeGridComponent } from './grids/company-fee-grid/company-fee-grid.component';
import { CompanyOfficesGridComponent } from './grids/company-offices-grid/company-offices-grid.component';
import { CompanyServiceRankingsGridComponent } from './grids/company-service-rankings-grid/company-service-rankings-grid.component';
import { CompanyVendorFilesGridComponent } from './grids/company-vendor-files-grid/company-vendor-files-grid.component';
import { CompanyVendorsGridComponent } from './grids/company-vendors-grid/company-vendors-grid.component';
import { ContactsGridComponent } from './grids/contacts-grid/contacts-grid.component';
import { ContractsGridComponent } from './grids/contracts-grid/contracts-grid.component';
import { ESignTemplatesGridComponent } from './grids/esign-templates-grid/esign-templates-grid.component';
import { ExemptVendorTypesGridComponent } from './grids/exempt-vendor-types-grid/exempt-vendor-types-grid.component';
import { ActionCellRendererComponent } from './grids/grid-components/cell-renderer-components/action-cell-renderer/action-cell-renderer.component';
import { FieldMappingCellRendererComponent } from './grids/grid-components/cell-renderer-components/field-mapping-cell-renderer/field-mapping-cell-renderer.component';
import { StarIconCellRendererComponent } from './grids/grid-components/cell-renderer-components/star-icon-cell-renderer/star-icon-cell-renderer.component';
import { ExportToolPanelComponent } from './grids/grid-components/tool-panel-components/export-tool-panel/export-tool-panel.component';
import { ViveRibbonIconCellRendererComponent } from './grids/grid-components/vive-ribbon-icon-cell-renderer/vive-ribbon-icon-cell-renderer.component';
import { InsuranceCertificatesGridComponent } from './grids/insurance-certificates-grid/insurance-certificates-grid.component';
import { InvoicesGridComponent } from './grids/invoices-grid/invoices-grid.component';
import { NotesGridComponent } from './grids/notes-grid/notes-grid.component';
import { PrimaryTradeLicenseRequirementsGridComponent } from './grids/primary-trade-license-requirements-grid/primary-trade-license-requirements-grid.component';
import { PrimaryTradesGridComponent } from './grids/primary-trades-grid/primary-trades-grid.component';
import { ProposalRequestVendorsGridComponent } from './grids/proposal-request-vendors-grid/proposal-request-vendors-grid.component';
import { ProposalRequestsGridComponent } from './grids/proposal-requests-grid/proposal-requests-grid.component';
import { SignedDocumentsGridComponent } from './grids/signed-documents-grid/signed-documents-grid.component';
import { SiteFilesGridComponent } from './grids/site-files-grid/site-files-grid.component';
import { SiteUsersGridComponent } from './grids/site-users-grid/site-users-grid.component';
import { SiteVendorFilesGridComponent } from './grids/site-vendor-files-grid/site-vendor-files-grid.component';
import { SiteVendorsGridComponent } from './grids/site-vendors-grid/site-vendors-grid.component';
import { SitesGridComponent } from './grids/sites-grid/sites-grid.component';
import { TradeCategoriesGridComponent } from './grids/trade-categories-grid/trade-categories-grid.component';
import { TradeSpecialtyCategoriesGridComponent } from './grids/trade-specialty-categories-grid/trade-specialty-categories-grid.component';
import { UsersGridComponent } from './grids/users-grid/users-grid.component';
import { VendorTradesGridComponent } from './grids/vendor-trades-grid/vendor-trades-grid.component';
import { VendorsGridComponent } from './grids/vendors-grid/vendors-grid.component';
import { HeaderComponent } from './header/header.component';
import { HttpInterceptorInterceptor } from './interceptors/http-interceptor.interceptor';
import { TermsAndConditionsInterceptorInterceptor } from './interceptors/terms-and-conditions-interceptor.interceptor';
import { ForgotPasswordComponent } from './login/forgot-password/forgot-password.component';
import { LoginComponent } from './login/login.component';
import { ResetPasswordComponent } from './login/reset-password/reset-password.component';
import { LogoutComponent } from './logout/logout.component';
import { AddClientRegisteredVendorModalComponent } from './modals/add-client-registered-vendor-modal/add-client-registered-vendor-modal.component';
import { AddEditClientRegisteredVendorModalComponent } from './modals/add-edit-client-registered-vendor-modal/add-edit-client-registered-vendor-modal.component';
import { AddEditCompanyFeeModalComponent } from './modals/add-edit-company-fee-modal/add-edit-company-fee-modal.component';
import { AddEditContactModalComponent } from './modals/add-edit-contact-modal/add-edit-contact-modal.component';
import { AddEditEsignTemplateModalComponent } from './modals/add-edit-esign-template-modal/add-edit-esign-template-modal.component';
import { AddEditExemptVendorModalComponent } from './modals/add-edit-exempt-vendor-modal/add-edit-exempt-vendor-modal.component';
import { AddEditExemptVendorTypeModalComponent } from './modals/add-edit-exempt-vendor-type-modal/add-edit-exempt-vendor-type-modal.component';
import { AddEditInsuranceAgentModalComponent } from './modals/add-edit-insurance-agent-modal/add-edit-insurance-agent-modal.component';
import { AddEditOwnerModalComponent } from './modals/add-edit-owner-modal/add-edit-owner-modal.component';
import { AddEditServiceRegionModalComponent } from './modals/add-edit-service-region-modal/add-edit-service-region-modal.component';
import { AddEditSiteModalComponent } from './modals/add-edit-site-modal/add-edit-site-modal.component';
import { AddEditSpotlightVendorRankingModalComponent } from './modals/add-edit-spotlight-vendor-ranking-modal/add-edit-spotlight-vendor-ranking-modal.component';
import { AddEditTradeCategoryComponent } from './modals/add-edit-trade-category/add-edit-trade-category.component';
import { AddEditTradeModalComponent } from './modals/add-edit-trade-modal/add-edit-trade-modal.component';
import { AddEditTradeRequirementModalComponent } from './modals/add-edit-trade-requirement-modal/add-edit-trade-requirement-modal.component';
import { AddEditTradeSpecialtyCategoryComponent } from './modals/add-edit-trade-specialty-category/add-edit-trade-specialty-category.component';
import { AddEditVendorInformationModalComponent } from './modals/add-edit-vendor-information-modal/add-edit-vendor-information-modal.component';
import { AddEditVendorReviewModalComponent } from './modals/add-edit-vendor-review-modal/add-edit-vendor-review-modal.component';
import { AddEditViveProgressModalComponent } from './modals/add-edit-vive-progress-modal/add-edit-vive-progress-modal.component';
import { AddProposalRequestNonViveVendorsModalComponent } from './modals/add-proposal-request-non-vive-vendors-modal/add-proposal-request-non-vive-vendors-modal.component';
import { AddProposalRequestViveVendorsModalComponent } from './modals/add-proposal-request-vive-vendors-modal/add-proposal-request-vive-vendors-modal.component';
import { AddSsoConnectionModalComponent } from './modals/add-sso-connection-modal/add-sso-connection-modal.component';
import { AdminClientSiteDetailsModalComponent } from './modals/admin-client-site-details-modal/admin-client-site-details-modal.component';
import { ApVendorClientManagedUploadModalComponent } from './modals/ap-vendor-client-managed-upload-modal/ap-vendor-client-managed-upload-modal.component';
import { ApVendorMapperApConfigSelectionModalComponent } from './modals/ap-vendor-mapper-ap-config-selection-modal/ap-vendor-mapper-ap-config-selection-modal.component';
import { AssignSitesToUserModalComponent } from './modals/assign-sites-to-user-modal/assign-sites-to-user-modal.component';
import { AssignSitesToVendorModalComponent } from './modals/assign-sites-to-vendor-modal/assign-sites-to-vendor-modal.component';
import { AssignUsersToSiteModalComponent } from './modals/assign-users-to-site-modal/assign-users-to-site-modal.component';
import { AssignVendorsToSiteModalComponent } from './modals/assign-vendors-to-site-modal/assign-vendors-to-site-modal.component';
import { BlockedTaxIdsModalComponent } from './modals/blocked-tax-ids-modal/blocked-tax-ids-modal.component';
import { CanadianWorkersCompensationModalComponent } from './modals/canadian-workers-compensation-modal/canadian-workers-compensation-modal.component';
import { CardColumnAccordionDisplayModalComponent } from './modals/card-column-modal/card-column-accordion-display-modal/card-column-accordion-display-modal.component';
import { CardColumnDisplayModalComponent } from './modals/card-column-modal/card-column-display-modal/card-column-display-modal.component';
import { ChooseExemptVendorTypeModalComponent } from './modals/choose-exempt-vendor-type-modal/choose-exempt-vendor-type-modal.component';
import { ClientRegisteredVendorsUploadModalComponent } from './modals/client-registered-vendors-upload-modal/client-registered-vendors-upload-modal.component';
import { ClientUsersUploadModalComponent } from './modals/client-users-upload-modal/client-users-upload-modal.component';
import { ClientVendorSearchModalComponent } from './modals/client-vendor-search-modal/client-vendor-search-modal.component';
import { CoiUploadModalComponent } from './modals/coi-upload-modal/coi-upload-modal.component';
import { CompanyOfficesModalComponent } from './modals/company-offices-modal/company-offices-modal.component';
import { CompanyVendorFileUploadModalComponent } from './modals/company-vendor-file-upload-modal/company-vendor-file-upload-modal.component';
import { ComplianceNoteModalComponent } from './modals/compliance-note-modal/compliance-note-modal.component';
import { ComplianceStatusModalComponent } from './modals/compliance-status-modal/compliance-status-modal.component';
import { ConfigureComplianceTypesModalComponent } from './modals/configure-compliance-types-modal/configure-compliance-types-modal.component';
import { ConfirmArchiveVendorModalComponent } from './modals/confirm-archive-vendor-modal/confirm-archive-vendor-modal.component';
import { ConfirmModalComponent } from './modals/confirm-modal/confirm-modal.component';
import { ConfirmRunBusinessVerificationModalComponent } from './modals/confirm-run-business-verification-modal/confirm-run-business-verification-modal.component';
import { ContractFileUploadModalComponent } from './modals/contract-file-upload-modal/contract-file-upload-modal.component';
import { CreateTicketModalComponent } from './modals/create-ticket-modal/create-ticket-modal.component';
import { DateRangeSelectionModalComponent } from './modals/date-range-selection-modal/date-range-selection-modal.component';
import { EditClientViveStatusModalComponent } from './modals/edit-client-vive-status-modal/edit-client-vive-status-modal.component';
import { EditContactTypeModalComponent } from './modals/edit-contact-type-modal/edit-contact-type-modal.component';
import { EditFeeVouchersModalComponent } from './modals/edit-fee-vouchers-modal/edit-fee-vouchers-modal.component';
import { EditLicenseModalComponent } from './modals/edit-license-modal/edit-license-modal.component';
import { EditProposalRequestVendorModalComponent } from './modals/edit-proposal-request-vendor-modal/edit-proposal-request-vendor-modal.component';
import { EditUserModalComponent } from './modals/edit-user-modal/edit-user-modal.component';
import { EditVendorAddressModalComponent } from './modals/edit-vendor-address-modal/edit-vendor-address-modal.component';
import { ExemptVendorsUploadModalComponent } from './modals/exempt-vendors-upload-modal/exempt-vendors-upload-modal.component';
import { FileUploadModalComponent } from './modals/file-upload-modal/file-upload-modal.component';
import { GetUserInputModalComponent } from './modals/get-user-input-modal/get-user-input-modal.component';
import { InsurancePolicySitesModalComponent } from './modals/insurance-policy-sites-modal/insurance-policy-sites-modal.component';
import { LicenseUploadSelectionModalComponent } from './modals/license-upload-selection-modal/license-upload-selection-modal.component';
import { LoginModalComponent } from './modals/login-modal/login-modal.component';
import { LogoFileUploadModalComponent } from './modals/logo-file-upload-modal/logo-file-upload-modal.component';
import { ManageAccountingLinksModalComponent } from './modals/manage-accounting-links-modal/manage-accounting-links-modal.component';
import { MarkVsaSignedModalComponent } from './modals/mark-vsa-signed-modal/mark-vsa-signed-modal.component';
import { NormalizeAddressesModalComponent } from './modals/normalize-addresses-modal/normalize-addresses-modal.component';
import { NoteDisplayModalComponent } from './modals/note-display-modal/note-display-modal.component';
import { NoteEmailDisplayModalComponent } from './modals/note-email-display-modal/note-email-display-modal.component';
import { NoteModalComponent } from './modals/note-modal/note-modal.component';
import { NoteTicketDisplayModalComponent } from './modals/note-ticket-display-modal/note-ticket-display-modal.component';
import { OperatingStatesModalComponent } from './modals/operating-states-modal/operating-states-modal.component';
import { PreferredVendorRankingSelectionModalComponent } from './modals/preferred-vendor-ranking-selection-modal/preferred-vendor-ranking-selection-modal.component';
import { ProposalRequestFileUploadModalComponent } from './modals/proposal-request-file-upload-modal/proposal-request-file-upload-modal.component';
import { SelectAccountingSystemsModalComponent } from './modals/select-accounting-systems-modal/select-accounting-systems-modal.component';
import { SelectClientsModalComponent } from './modals/select-clients-modal/select-clients-modal.component';
import { SelectComplianceProgramModalComponent } from './modals/select-compliance-program-modal/select-compliance-program-modal.component';
import { SignedDocumentFileDownloadModalComponent } from './modals/signed-document-file-download-modal/signed-document-file-download-modal.component';
import { SignedDocumentUploadModalComponent } from './modals/signed-document-upload-modal/signed-document-upload-modal.component';
import { SitesUploadModalComponent } from './modals/sites-upload-modal/sites-upload-modal.component';
import { TermsAndConditionsModalComponent } from './modals/terms-and-conditions-modal/terms-and-conditions-modal.component';
import { TradesModalComponent } from './modals/trades-modal/trades-modal.component';
import { TransactionModalComponent } from './modals/transaction-modal/transaction-modal.component';
import { UnlinkApVendorsModalComponent } from './modals/unlink-ap-vendors-modal/unlink-ap-vendors-modal.component';
import { VendorInviteModalComponent } from './modals/vendor-invite-modal/vendor-invite-modal.component';
import { VendorTradeStatesModalComponent } from './modals/vendor-trade-states-modal/vendor-trade-states-modal.component';
import { VerifyBusinessChecksModalComponent } from './modals/verify-business-checks-modal/verify-business-checks-modal.component';
import { VideoPlayerModalComponent } from './modals/video-player-modal/video-player-modal.component';
import { NavSubMenuComponent } from './nav/nav-sub-menu/nav-sub-menu.component';
import { NavComponent } from './nav/nav.component';
import { AuthService } from './services/auth.service';
import { GlobalErrorHandler } from './services/global-error-handler.service';
import { SsoService } from './services/sso.service';
import { SessionUnavailableComponent } from './session-unavailable/session-unavailable.component';
import { AddEditClientRegisteredVendorComponent } from './shared-components/add-edit-client-registered-vendor/add-edit-client-registered-vendor.component';
import { AddEditClientComponent } from './shared-components/add-edit-client/add-edit-client.component';
import { AddEditExemptVendorComponent } from './shared-components/add-edit-exempt-vendor/add-edit-exempt-vendor.component';
import { AddEditProposalRequestVendorComponent } from './shared-components/add-edit-proposal-request-vendor/add-edit-proposal-request-vendor.component';
import { AddEditSiteComponent } from './shared-components/add-edit-site/add-edit-site.component';
import { AppContainerComponent } from './shared-components/app-container/app-container.component';
import { ButtonBannerComponent } from './shared-components/banner/button-banner/button-banner.component';
import { NotificationBannerComponent } from './shared-components/banner/notification-banner/notification-banner.component';
import { CardColumnComponent } from './shared-components/card-column/card-column.component';
import { CardComponent } from './shared-components/card/card.component';
import { ChangeLogComponent } from './shared-components/change-log/change-log.component';
import { ChartsComponent } from './shared-components/charts/charts.component';
import { ClientRegisteredVendorComplianceComponent } from './shared-components/client-registered-vendor-compliance/client-registered-vendor-compliance.component';
import { ClientServiceAgreementComponent } from './shared-components/client-service-agreement/client-service-agreement.component';
import { ClientVendorComplianceInfoComponent } from './shared-components/client-vendor-compliance-info/client-vendor-compliance-info.component';
import { InsuranceInfoDisplayCardComponent } from './shared-components/client-vendor-compliance/insurance-info-display-card/insurance-info-display-card.component';
import { ClientVendorOfficeSelectionComponent } from './shared-components/client-vendor-office-selection/client-vendor-office-selection.component';
import { ClientVendorSearchComponent } from './shared-components/client-vendor-search/client-vendor-search.component';
import { ComplianceTrackerComponent } from './shared-components/compliance-tracker/compliance-tracker.component';
import { ContractDetailsComponent } from './shared-components/contract-details/contract-details.component';
import { DashboardComponent } from './shared-components/dashboard/dashboard.component';
import { ChartWidgetComponent } from './shared-components/dashboard/widget/chart-widget/chart-widget.component';
import { DisplayCountsWidgetComponent } from './shared-components/dashboard/widget/display-counts-widget/display-counts-widget.component';
import { ListItemsWidgetComponent } from './shared-components/dashboard/widget/list-items-widget/list-items-widget.component';
import { TitleContentWidgetComponent } from './shared-components/dashboard/widget/title-content-widget/title-content-widget.component';
import { WidgetContainerDirective } from './shared-components/dashboard/widget/widget-container/widget-container.directive';
import { WidgetPanelComponent } from './shared-components/dashboard/widget/widget-panel/widget-panel.component';
import { DisplayCardColumnComponent } from './shared-components/display-card-column/display-card-column.component';
import { DisplayCardComponent } from './shared-components/display-card/display-card.component';
import { EmailContainerComponent } from './shared-components/email-container/email-container.component';
import { EmailTagfieldComponent } from './shared-components/email-tagfield/email-tagfield.component';
import { FileUploadFileDisplayComponent } from './shared-components/file-upload-file-display/file-upload-file-display.component';
import { GeoMapComponent } from './shared-components/geo-map/geo-map.component';
import { InsuranceAgentEditComponent } from './shared-components/insurance-agent-edit/insurance-agent-edit.component';
import { InsuranceCertificateFindingAndResultsComponent } from './shared-components/insurance-certificate-finding-and-results/insurance-certificate-finding-and-results.component';
import { InteractableDisplayListComponent } from './shared-components/interactable-display-list/interactable-display-list.component';
import { LoadingMaskComponent } from './shared-components/loading-mask/loading-mask.component';
import { MultiSelectComponent } from './shared-components/multi-select/multi-select.component';
import { OwnerEditComponent } from './shared-components/owner-edit/owner-edit.component';
import { PasswordChangeComponent } from './shared-components/password-change/password-change.component';
import { PasswordFieldComponent } from './shared-components/password-field/password-field.component';
import { PasswordValidatorComponent } from './shared-components/password-validator/password-validator.component';
import { QuicksourceDownloadsComponent } from './shared-components/quicksource-downloads/quicksource-downloads.component';
import { QuicksourceTrackerComponent } from './shared-components/quicksource-tracker/quicksource-tracker.component';
import { RegistrationTrackerComponent } from './shared-components/registration-tracker/registration-tracker.component';
import { RowcalPreferredVendorTermsComponent } from './shared-components/rowcal-preferred-vendor-terms/rowcal-preferred-vendor-terms.component';
import { SelectDropdownComponent } from './shared-components/select-dropdown/select-dropdown.component';
import { SsoLoginButtonComponent } from './shared-components/sso-login-button/sso-login-button.component';
import { StarRatingComponent } from './shared-components/star-rating/star-rating.component';
import { ToastComponent } from './shared-components/toast/toast.component';
import { TradeSelectionDropdownComponent } from './shared-components/trade-selection-dropdown/trade-selection-dropdown.component';
import { UserComponent } from './shared-components/users/user/user.component';
import { UsersComponent } from './shared-components/users/users.component';
import { VendorAddressEditComponent } from './shared-components/vendor-address-edit/vendor-address-edit.component';
import { VendorDocumentsNotesComponent } from './shared-components/vendor-documents-notes/vendor-documents-notes.component';
import { VendorEditComponent } from './shared-components/vendor-edit/vendor-edit.component';
import { VendorInformationComponent } from './shared-components/vendor-information/vendor-information.component';
import { VendorOperatingStatesComponent } from './shared-components/vendor-operating-states/vendor-operating-states.component';
import { VendorReviewQuestionsComponent } from './shared-components/vendor-review-questions/vendor-review-questions.component';
import { VendorSearchContainerComponent } from './shared-components/vendor-search-container/vendor-search-container.component';
import { VendorSiteAssignmentComponent } from './shared-components/vendor-site-assignment/vendor-site-assignment.component';
import { ZendeskTicketComponent } from './shared-components/zendesk-ticket/zendesk-ticket.component';
import viveApiConfig from './shared/vive-api/vive-api.config';
import { ProposalComponent } from './vendor/quicksource/proposals/proposal/proposal.component';
import { ProposalsComponent } from './vendor/quicksource/proposals/proposals.component';

// End Directives

ModuleRegistry.registerModules([AllEnterpriseModule]);

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-086083}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Vendor_Information_Verification_Experts,_LLC}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{VIVE}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{VIVE}_need_to_be_licensed___{VIVE}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{7_April_2026}____[v3]_[01]_MTc3NTUxNjQwMDAwMA==caa809179e82754a17c346a0061eaa3a'
);

provideGlobalGridOptions({ theme: 'legacy' });

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SsoLoginButtonComponent,
    ClientComponent,
    AdminComponent,
    LoginModalComponent,
    DashboardComponent,
    WidgetContainerDirective,
    WidgetPanelComponent,
    NotificationCenterComponent,
    ClientDashboardComponent,
    AdminDashboardComponent,
    BaseGridComponent,
    ClientsGridComponent,
    VendorsGridComponent,
    UsersGridComponent,
    CardColumnComponent,
    CardComponent,
    AppContainerComponent,
    ToastComponent,
    ConfirmModalComponent,
    NgbDateInputMaskDirective,
    VendorTradeStatesModalComponent,
    VendorOperatingStatesComponent,
    OperatingStatesModalComponent,
    CompanyOfficesModalComponent,
    SpotlightVendorsWidgetComponent,
    VendorStatusesWidgetComponent,
    ContractsWidgetComponent,
    QuickSourceWidgetComponent,
    ChartsComponent,
    VendorRatingsWidgetComponent,
    VendorTradesWidgetComponent,
    AlertsMetricsWidgetComponent,
    InteractableDisplayListComponent,
    TradesModalComponent,
    CardColumnDisplayModalComponent,
    CardColumnAccordionDisplayModalComponent,
    DisplayCardComponent,
    FileUploadFileDisplayComponent,
    LicenseUploadSelectionModalComponent,
    DisplayCardColumnComponent,
    RegistrationTrackerComponent,
    RowcalPreferredVendorTermsComponent,
    ClientServiceAgreementComponent,
    NavComponent,
    NavSubMenuComponent,
    LogoutComponent,
    SessionUnavailableComponent,
    TitleContentWidgetComponent,
    DisplayCountsWidgetComponent,
    ListItemsWidgetComponent,
    ChartWidgetComponent,
    HeaderComponent,
    ClientMyVendorsComponent,
    ClientViveVendorsComponent,
    ClientMyVendor,
    SpecificVendorComplianceFindingDropdownComponent,
    FooterComponent,
    ButtonBannerComponent,
    NotificationBannerComponent,
    VendorsComponent,
    ClientsComponent,
    ComplianceComponent,
    ReportsComponent,
    AdminDashboardsComponent,
    CompanyVendorsGridComponent,
    ClientMyVendorInsuranceComponent,
    StatusFieldChangesDirective,
    ClientMyVendorTradesAndLicensesComponent,
    ActionCellRendererComponent,
    InsuranceInfoDisplayCardComponent,
    UsersComponent,
    UserComponent,
    ClientMyVendorSitesComponent,
    SitesGridComponent,
    ClientSitesComponent,
    ProposalRequestsGridComponent,
    ProposalRequestVendorsGridComponent,
    ProposalsComponent,
    ProposalComponent,
    ComplianceBusinessReviewComponent,
    ComplianceOwnerReviewComponent,
    ComplianceTradeReviewComponent,
    ComplianceInsuranceReviewComponent,
    ComplianceReviewComponent,
    ExpiringInsuranceWidgetComponent,
    VendorRenewalsWidgetComponent,
    QualityControlWidgetComponent,
    ReviewingVendorsWidgetComponent,
    VendorStatusesChartWidgetComponent,
    VendorSuspensionsWidgetComponent,
    PendingReviewsWidgetComponent,
    ResolvingVendorsWidgetComponent,
    ApiErrorsWidgetComponent,
    ClientMyVendorRatingsComponent,
    StarRatingComponent,
    AddEditVendorReviewModalComponent,
    VendorReviewQuestionsComponent,
    ZendeskTicketComponent,
    AccountingAlertsWidgetComponent,
    ContractsGridComponent,
    ClientMyVendorContractsComponent,
    ClientMyVendorContractDetailsComponent,
    ClientMyVendorQsbComponent,
    ProposalRequestComponent,
    ClientMyVendorHistoryComponent,
    NotesGridComponent,
    ClientMyVendorDocumentsComponent,
    CompanyVendorFilesGridComponent,
    ClientMyVendorContactsComponent,
    ContactsGridComponent,
    ClientMyVendorAdminComponent,
    ManageAccountingLinksModalComponent,
    ApVendorsGridComponent,
    ChooseExemptVendorTypeModalComponent,
    PreferredVendorRankingSelectionModalComponent,
    VendorInformationComponent,
    ClientVendorComplianceInfoComponent,
    GeoMapComponent,
    ClientVendorsComponent,
    AddClientRegisteredVendorModalComponent,
    SelectComplianceProgramModalComponent,
    NormalizeAddressesModalComponent,
    NoteModalComponent,
    ComplianceStatusModalComponent,
    MarkVsaSignedModalComponent,
    CreateTicketModalComponent,
    QuicksourceComponent,
    ConfirmRunBusinessVerificationModalComponent,
    QuicksourceSiteSelectionComponent,
    QuicksourceLocationAndContactComponent,
    QuicksourceDatePricingInstructionsComponent,
    QuicksourceAddVendorsComponent,
    QuicksourceReviewComponent,
    QuicksourceCreationComponent,
    ProposalRequestFileUploadModalComponent,
    CoiUploadModalComponent,
    AddProposalRequestViveVendorsModalComponent,
    AddProposalRequestNonViveVendorsModalComponent,
    ComplianceTrackerComponent,
    ComplianceFinalReviewComponent,
    AddEditOwnerModalComponent,
    OwnerEditComponent,
    VendorEditComponent,
    AddEditVendorInformationModalComponent,
    EditLicenseModalComponent,
    AddEditInsuranceAgentModalComponent,
    InsuranceAgentEditComponent,
    AddEditCOIComponent,
    LoadingMaskComponent,
    ContractsComponent,
    ContractComponent,
    ContractSiteSelectionComponent,
    ContractVendorSelectionComponent,
    ContractReviewComponent,
    ContractAddContractComponent,
    ContractDetailsComponent,
    ContractFileUploadModalComponent,
    AddSiteComponent,
    AddEditSiteComponent,
    AddressFormComponent,
    EditSiteComponent,
    SiteComponent,
    SiteDetailsComponent,
    SiteContactsComponent,
    AddEditContactModalComponent,
    SiteVendorsComponent,
    SiteUsersComponent,
    SiteAdminComponent,
    AssignVendorsToSiteModalComponent,
    AssignUsersToSiteModalComponent,
    SiteVendorsGridComponent,
    SiteUsersGridComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    PasswordValidatorComponent,
    BusinessVerificationApiErrorsComponent,
    ApSystemsComponent,
    BusinessVerificationApiErrorsGridComponent,
    ApConfigsGridComponent,
    PasswordFieldComponent,
    ComplianceSettingsComponent,
    ClientInfoComponent,
    GetUserInputModalComponent,
    LogoFileUploadModalComponent,
    ComplianceCompanyBackgroundChecksComponent,
    ComplianceOwnerBackgroundChecksComponent,
    ComplianceGeneralLiabilityComponent,
    ComplianceAutoLiabilityComponent,
    ComplianceUmbrellaLiabilityComponent,
    ComplianceWorkersCompensationComponent,
    ComplianceProfessionalLiabilityComponent,
    ComplianceTradeSpecificComplianceTiersComponent,
    ConfigureComplianceTypesModalComponent,
    AdminVendorComponent,
    InsurancePolicySitesModalComponent,
    SiteVendorsValidatedComponent,
    AdminVendorInfoComponent,
    AdminVendorContactsComponent,
    AdminVendorClientsComponent,
    AdminVendorOwnersComponent,
    AdminVendorInvoicesComponent,
    AdminVendorAdminComponent,
    CanadianWorkersCompensationModalComponent,
    ComplianceBusinessVerifiedCompanyBackgroundChecksComponent,
    EditContactTypeModalComponent,
    InvoicesGridComponent,
    TransactionModalComponent,
    EmailContainerComponent,
    ClientAdminComponent,
    ClientAdminAccountingComponent,
    ClientAdminAccountingSystemComponent,
    ClientAdminAccountingAllApVendorsComponent,
    ClientAdminMyCompanyComponent,
    FormControlNavtiveElementDirective,
    AddEditClientComponent,
    AddClientComponent,
    ClientAdminPublicApiKeysComponent,
    ClientAdminSpotlightVendorsComponent,
    ClientAdminComplianceExemptSetupComponent,
    CompanyServiceRankingsGridComponent,
    AddEditSpotlightVendorRankingModalComponent,
    ExemptVendorTypesGridComponent,
    AddEditExemptVendorTypeModalComponent,
    AdminClientComponent,
    AdminClientNotesComponent,
    AdminClientContactsComponent,
    AdminClientSitesComponent,
    AdminClientServiceRegionsComponent,
    AdminClientVendorsComponent,
    AdminClientExemptVendorsComponent,
    AdminClientClientRegisteredVendorsComponent,
    AdminClientAdminComponent,
    CompanyOfficesGridComponent,
    AddEditExemptVendorComponent,
    AddEditClientRegisteredVendorComponent,
    MyVendorsWidgetComponent,
    ViveDirectoryWidgetComponent,
    ClientVendorSearchComponent,
    VendorSearchContainerComponent,
    ClientVendorSearchModalComponent,
    StarIconCellRendererComponent,
    FieldMappingCellRendererComponent,
    VendorRatingsComponent,
    TermsAndConditionsModalComponent,
    VendorInviteModalComponent,
    AdminAdminComponent,
    AdminClientSiteDetailsModalComponent,
    FileUploadModalComponent,
    SitesUploadModalComponent,
    AddEditSiteModalComponent,
    RequiredAsteriskDirective,
    AssignSitesToUserModalComponent,
    QuicksourceTrackerComponent,
    EmailTagfieldComponent,
    NoteEmailDisplayModalComponent,
    NoteTicketDisplayModalComponent,
    NoteDisplayModalComponent,
    CompanyFeeGridComponent,
    EditClientViveStatusModalComponent,
    VerifyBusinessChecksModalComponent,
    EditFeeVouchersModalComponent,
    BlockedTaxIdsModalComponent,
    AddEditCompanyFeeModalComponent,
    ClientUsersUploadModalComponent,
    EditUserModalComponent,
    ConfirmArchiveVendorModalComponent,
    AdminAdminTradesComponent,
    PrimaryTradesGridComponent,
    PrimaryTradeLicenseRequirementsGridComponent,
    TradeCategoriesGridComponent,
    AddEditTradeModalComponent,
    AdminAdminTradeRequirementsComponent,
    AddEditTradeRequirementModalComponent,
    AdminAdminTradeCategoriesComponent,
    AddEditTradeCategoryComponent,
    AdminAdminTradeSpecialtyCategoriesComponent,
    TradeSpecialtyCategoriesGridComponent,
    AddEditTradeSpecialtyCategoryComponent,
    ExportToolPanelComponent,
    AddEditServiceRegionModalComponent,
    CurrencyInputDirective,
    AddEditViveProgressModalComponent,
    CompanyVendorFileUploadModalComponent,
    ClientReportsComponent,
    AddEditProposalRequestVendorComponent,
    EditProposalRequestVendorModalComponent,
    AdminVendorDocumentsComponent,
    AdminVendorNotesComponent,
    VendorDocumentsNotesComponent,
    DateRangeSelectionModalComponent,
    ChangeLogComponent,
    AdminAdminClientApConfigsComponent,
    AdminAdminEmailsComponent,
    SelectClientsModalComponent,
    SelectAccountingSystemsModalComponent,
    MultiSelectComponent,
    AdminClientApConfigsComponent,
    QuicksourceDownloadsComponent,
    VendorTradesGridComponent,
    InsuranceCertificatesGridComponent,
    PasswordChangeComponent,
    ClientRegisteredVendorsUploadModalComponent,
    AddEditClientRegisteredVendorModalComponent,
    AddEditExemptVendorModalComponent,
    ApVendorClientManagedUploadModalComponent,
    ExemptVendorsUploadModalComponent,
    AdminAdminEsignTemplatesComponent,
    AdminAdminSsoConnectionsComponent,
    AdminAdminSsoConnectionComponent,
    SsoConnectionDomainsCardComponent,
    SsoConnectionRoleMappingsCardComponent,
    AddSsoConnectionModalComponent,
    ESignTemplatesGridComponent,
    AddEditEsignTemplateModalComponent,
    SignedDocumentsGridComponent,
    VendorAddressEditComponent,
    EditVendorAddressModalComponent,
    ClientVendorOfficeSelectionComponent,
    AdminComplianceClientRegisteredVendorsComponent,
    AdminComplianceExemptVendorComponent,
    ClientRegisteredVendorComplianceComponent,
    ComplianceNoteModalComponent,
    TradeSelectionDropdownComponent,
    VendorSiteAssignmentComponent,
    AssignSitesToVendorModalComponent,
    VideoPlayerModalComponent,
    InsuranceCertificateFindingAndResultsComponent,
    ViveRibbonIconCellRendererComponent,
    SiteFilesComponent,
    SiteFilesGridComponent,
    UnlinkApVendorsModalComponent,
    SelectDropdownComponent,
    SiteVendorFilesComponent,
    SiteVendorFilesGridComponent,
    SignedDocumentFileDownloadModalComponent,
    SignedDocumentUploadModalComponent,
    ApVendorMapperApConfigSelectionModalComponent
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    AgGridModule,
    InputMaskCustomModule,
    CommonModule
  ],
  providers: [
    provideZonelessChangeDetection(),
    // Identify the session main.ts seeded (Pendo, Chameleon, Datadog RUM,
    // PostHog) before the first view renders, and follow it when another tab
    // changes it.
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      authService.identify();
      authService.watchBrowserSession();
    }),
    // The generated ViveApi client sends its requests through this HttpClient,
    // so the interceptors above apply to them, and throws if one goes out
    // before this runs.
    provideAppInitializer(() => {
      viveApiConfig.configure(inject(HttpClient));
    }),
    // Reads and strips ?sso_error= before the router's first navigation, so an
    // auth-guard redirect's returnUrl never carries it.
    provideAppInitializer(() =>
      inject(SsoService).consumeErrorFromUrl(
        !inject(AuthService).isAuthenticated()
      )
    ),
    CurrencyPipe,
    DatePipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TermsAndConditionsInterceptorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorInterceptor,
      multi: true
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // DataDog RUM: route -> view tracking (inert until datadogRum.init() runs,
    // gated on the datadog env config in main.ts).
    provideDatadogRouter(),
    // Recover from stale-chunk module-load failures (reload once, guarded) and
    // forward errors to RUM. Replaces provideDatadogErrorHandler(): ErrorHandler
    // is a single-value DI token, so GlobalErrorHandler calls addAngularError()
    // itself to keep RUM error reporting.
    { provide: ErrorHandler, useClass: GlobalErrorHandler }
  ]
})
export class AppModule {}
